/**
 * @file The "Sales credit by month" chart model: one month per server month
 * start of the period, booked from `salesClosedByMonth`'s `column` (the
 * level's `ANALYTICS_BOOKED_COLUMN`: sales credit at My sales, the whole
 * booked amount at My team and Org) and quoted (Quote and Buy together) from
 * `quoteValueByMonth`. Consumer: the Overview chart. A
 * month with no rows reads `"0"`, a month number outside the period is
 * dropped, the last month is the current one, and labels are read in
 * `timeZone`. A quoted answer that does not answer leaves booked standing and
 * marks each month's quoted with its own state, never zero.
 */
import { DateTime } from 'luxon';
import type { Snapshot } from 'valtio';

import type { ANALYTICS_BOOKED_COLUMN, AnalyticsScopeLevel } from '#types';

import { analyticsAnswerFigure } from './analyticsAnswerFigure';
import { analyticsMonthStarts } from './analyticsMonthStarts';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type { AnalyticsMonth } from './analyticsOverviewTab.types';
import type { AnalyticsAnswer, AnalyticsUnansweredFigure } from './index.types';

export const analyticsMonths = (
  booked: Snapshot<AnalyticsAnswer> | undefined,
  column: (typeof ANALYTICS_BOOKED_COLUMN)[AnalyticsScopeLevel],
  quoted: Snapshot<AnalyticsAnswer> | undefined,
  timeZone: string,
):
  | { status: 'answered'; months: AnalyticsMonth[] }
  | AnalyticsUnansweredFigure => {
  if (booked?.status !== 'answered') return analyticsUnansweredFigure(booked);
  if (!DateTime.now().setZone(timeZone).isValid)
    return {
      status: 'refused',
      answer: {
        status: 'failed',
        message: `The analytics time zone ${timeZone} is not a known zone.`,
      },
    };
  const starts = analyticsMonthStarts(booked.result.period, timeZone);
  const bookedFigures = starts.map((_, index) =>
    analyticsAnswerFigure(booked, column, (row) => row.month === index + 1),
  );
  const unanswered = bookedFigures.find(
    (figure): figure is AnalyticsUnansweredFigure =>
      figure.status !== 'answered',
  );
  if (unanswered !== undefined) return unanswered;
  const bookedDecimals = bookedFigures.flatMap((figure) =>
    figure.status === 'answered' ? [figure.decimal] : [],
  );
  return {
    status: 'answered',
    months: starts.map((start, index) => {
      const local = DateTime.fromISO(start, { zone: timeZone }).setLocale(
        'en-US',
      );
      return {
        month: index + 1,
        start,
        label: local.toFormat('LLL'),
        title: local.toFormat('LLLL yyyy'),
        booked: bookedDecimals[index],
        quoted: analyticsAnswerFigure(
          quoted,
          'quotedAmount',
          (row) => row.month === index + 1,
        ),
        isCurrent: index === starts.length - 1,
      };
    }),
  };
};
